import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AppError } from '@/utils/error/errorHandler';

export interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The error to display
   */
  error: AppError | Error | string;
  
  /**
   * Function to retry the operation
   */
  onRetry?: () => void;
  
  /**
   * Whether to show a retry button
   */
  showRetry?: boolean;
  
  /**
   * Whether to show the full error details
   */
  showDetails?: boolean;
  
  /**
   * Custom title for the error
   */
  title?: string;
}

/**
 * A reusable error state component
 */
export function ErrorState({
  error,
  onRetry,
  showRetry = true,
  showDetails = false,
  title = 'An error occurred',
  className,
  ...props
}: ErrorStateProps) {
  // Extract error message
  const errorMessage = typeof error === 'string' 
    ? error 
    : error.message || 'Unknown error';
  
  // Extract error details for AppError
  const errorDetails = (error instanceof AppError && showDetails) 
    ? {
        type: error.type,
        statusCode: error.statusCode,
        context: error.context ? JSON.stringify(error.context) : undefined
      }
    : undefined;
  
  return (
    <Alert variant="destructive" className={cn('max-w-md mx-auto', className)} {...props}>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <div className="mt-2">
          <p>{errorMessage}</p>
          
          {errorDetails && (
            <pre className="mt-2 p-2 bg-destructive/10 rounded text-xs overflow-auto">
              {JSON.stringify(errorDetails, null, 2)}
            </pre>
          )}
          
          {showRetry && onRetry && (
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4"
              onClick={onRetry}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
} 